import { NavLink } from 'react-router-dom'
import { Calendar, Clock, Home, KeyRound, Receipt, Sun } from 'lucide-react'
import { clsx } from 'clsx'

/**
 * MobileBottomNav (F5)
 *
 * Bottom navigation fixa em mobile (md:hidden). 5 atalhos enxutos para o
 * colaborador no celular:
 *  - Home: hub mobile
 *  - Escala: visao read-only da propria escala
 *  - Vales: meu painel (consumo / vale)
 *  - Dia (módulo dia_colaborador ligado) ou Ponto: abrir/fechar o dia com checklist
 *  - PIN: definir/trocar o PIN do app e celulares lembrados (leva também à senha)
 *
 * Montado pelo AppShell só para `isEmployeeOnly` com o módulo
 * `dia_colaborador` ligado (ver `showEmployeeBottomNav`). Altura ~64px; o
 * <main> recebe pb-20 md:pb-6 pra nao cobrir conteudo.
 */

type NavItem = {
  to: string
  label: string
  icon: typeof Home
  end?: boolean
}

const baseItems: NavItem[] = [
  { to: '/app/mobile', label: 'Home', icon: Home, end: true },
  { to: '/app/my-schedule', label: 'Escala', icon: Calendar },
  { to: '/app/my-panel', label: 'Vales', icon: Receipt },
]

const pontoItem: NavItem = { to: '/app/time-records', label: 'Ponto', icon: Clock }
const diaItem: NavItem = { to: '/app/dia', label: 'Dia', icon: Sun }
// PWA (29/10/2026): o PIN é a credencial do colaborador; a tela /app/pin
// também leva à troca de senha do sistema.
const perfilItem: NavItem = { to: '/app/pin', label: 'PIN', icon: KeyRound }

type Props = {
  /** Colaborador comum com o módulo Dia ligado: "Ponto" vira "Dia" (/app/dia). */
  showDia?: boolean
}

export function MobileBottomNav({ showDia = false }: Props) {
  const items = [...baseItems, showDia ? diaItem : pontoItem, perfilItem]
  return (
    <nav
      aria-label="Navegação rápida"
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-border-color bg-surface shadow-[0_-4px_24px_rgba(0,0,0,0.12)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="mx-auto grid h-16 max-w-3xl grid-cols-5">
        {items.map(({ to, label, icon: Icon, end }) => (
          <li key={to} className="flex">
            <NavLink
              to={to}
              end={end as boolean | undefined}
              className={({ isActive }) =>
                clsx(
                  'flex flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium transition-colors',
                  isActive
                    ? 'text-brand'
                    : 'text-foreground-muted hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={clsx(
                      'h-5 w-5',
                      isActive ? 'text-brand' : 'text-foreground-muted',
                    )}
                    aria-hidden="true"
                  />
                  <span className="leading-none">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default MobileBottomNav
